import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../context';
import { MediaStatus, STATUS_REGISTRATION_REQUIREMENT } from '../../utils';
import { BrollCaptionVideo, Status } from '../../@types';

interface GetBrollCaptionProgressProps {
  uploadPercentage?: number;
  id: string;
  setOverallPercentage: (number) => void;
  initialStatus?: string;
  isRendering?: boolean;
  refreshBrollData?: () => void;
}

export const useGetBrollCaptionProgress = ({
  uploadPercentage = 0,
  id,
  setOverallPercentage,
  initialStatus,
  isRendering,
  refreshBrollData,
}: GetBrollCaptionProgressProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [videoDataStatus, setVideoDataStatus] = useState<string | null>(initialStatus || null);
  const [updatedBrollData, setUpdatedBrollData] = useState<{
    renderedUrl: string;
  } | null>(null);

  const {
    websocket: { brollCaptionTaskMap },
  } = useAppContext();

  const updateOverallProgress = () => {
    if (!videoDataStatus) {
      const progress = Math.round((uploadPercentage * 40) / 100);
      setOverallPercentage(progress);
    }
  };

  const { registrationRequired } = STATUS_REGISTRATION_REQUIREMENT;

  const isInProgress = registrationRequired.includes(initialStatus);

  const handleVideoDataStatus = () => {
    const totalDuration = 15 * 60 * 1000;
    const intervalDuration = 5000;
    const captionIncrement = (79 - 50) / (totalDuration / intervalDuration);

    switch (videoDataStatus) {
      case Status.NotStarted:
        setOverallPercentage(isRendering ? 0 : 40);
        break;

      case Status.ExtractingAudio:
        setOverallPercentage(45);
        break;

      case Status.ExtractingCaption:
        setOverallPercentage(50);
        startIncrementalProgress(captionIncrement, 79);
        break;

      case Status.GeneratingBroll:
        setOverallPercentage(80);
        break;

      case Status.ApplyingAIBroll:
        setOverallPercentage(90);
        break;

      case Status.Success:
        setOverallPercentage(100);
        break;

      default:
        if (isRendering) setOverallPercentage(0);
        break;
    }
  };

  const startIncrementalProgress = (increment: number, limit: number) => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      setOverallPercentage((prev) => {
        const next = prev + increment;

        if (next >= limit) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          return limit;
        }
        return next;
      });
    }, 1000);
  };

  useEffect(() => updateOverallProgress(), [uploadPercentage]);

  useEffect(() => {
    handleVideoDataStatus();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [videoDataStatus, id, isInProgress]);

  useEffect(() => {
    if (!id) return;
    const resData = brollCaptionTaskMap.get(id);
    if (!resData) return;

    if (isRendering) {
      setOverallPercentage(resData.progress);
    }

    if (resData.status === Status.Success) {
      setVideoDataStatus(Status.Success);
      setUpdatedBrollData(resData?.data);
      refreshBrollData && refreshBrollData();
    } else if (resData.status === Status.Error) {
      setVideoDataStatus(Status.Error);
      setUpdatedBrollData({ renderedUrl: '' });
    } else {
      setVideoDataStatus(resData?.data?.task || resData?.status);
    }
  }, [brollCaptionTaskMap.get(id)]);

  useEffect(() => {
    if (initialStatus) {
      setVideoDataStatus(initialStatus);
    }
  }, [initialStatus]);

  return {
    videoDataStatus,
    renderUrl: updatedBrollData?.renderedUrl,
  };
};
